/**
 * Сервис для работы со статистикой LLM
 */

import axios from 'axios';

// Типы для статистики LLM
export interface LLMCostData {
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCost: {
    inputCost: number;
    outputCost: number;
    totalCost: number;
  };
}

export interface LLMRequest {
  taskId: string;
  requestId: string;
  timestamp: string;
  endpoint: string;
  operation: string;
  costData: LLMCostData;
  descriptionLength: number;
  hasQuestion: boolean;
  packageName: string;
  variablesCount: number;
}

export interface LLMStats {
  taskId: string;
  createdAt: string;
  lastUpdated: string;
  totalRequests: number;
  totalTokens: number;
  totalCost: number;
  avgTokensPerRequest: number;
  avgCostPerRequest: number;
  modelStats: Record<string, {
    count: number;
    totalTokens: number;
    totalCost: number;
  }>;
  packageStats: Record<string, {
    count: number;
    totalTokens: number;
    totalCost: number;
  }>;
  requests: LLMRequest[];
}

// Ключ для хранения массива taskId в sessionStorage
const TASK_IDS_SESSION_KEY = 'plinus_analysis_task_ids';

class LLMStatsService {
  /**
   * Получить массив taskId из sessionStorage
   */
  getTaskIds(): string[] {
    try {
      const stored = sessionStorage.getItem(TASK_IDS_SESSION_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Ошибка при получении taskId из sessionStorage:', error);
      return [];
    }
  }

  /**
   * Сохранить массив taskId в sessionStorage
   */
  setTaskIds(taskIds: string[]): void {
    try {
      sessionStorage.setItem(TASK_IDS_SESSION_KEY, JSON.stringify(taskIds));
    } catch (error) {
      console.error('Ошибка при сохранении taskId в sessionStorage:', error);
    }
  }

  /**
   * Очистить массив taskId (при начале нового анализа)
   */
  clearTaskIds(): void {
    try {
      sessionStorage.removeItem(TASK_IDS_SESSION_KEY);
    } catch (error) {
      console.error('Ошибка при очистке taskId из sessionStorage:', error);
    }
  }

  /**
   * Добавить taskId к существующему массиву
   */
  addTaskId(taskId: string): void {
    const currentTaskIds = this.getTaskIds();
    if (!currentTaskIds.includes(taskId)) {
      currentTaskIds.push(taskId);
      this.setTaskIds(currentTaskIds);
    }
  }

  /**
   * Установить массив taskId (при начале нового анализа)
   */
  setInitialTaskId(taskId: string): void {
    this.setTaskIds([taskId]);
  }

  /**
   * Получить статистику по конкретному taskId
   */
  async getStatsForTaskId(taskId: string): Promise<LLMStats> {
    try {
      // Используем share-server для получения статистики
      const shareServerUrl = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';
      const response = await axios.get(`${shareServerUrl}/api/llm-stats/${taskId}`);
      return response.data;
    } catch (error) {
      console.error(`Ошибка при получении статистики для taskId ${taskId}:`, error);
      throw error;
    }
  }

  /**
   * Получить агрегированную статистику по всем taskId
   */
  async getAggregatedStats(taskIds: string[]): Promise<{
    totalTaskIds: number;
    totalRequests: number;
    totalTokens: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalInputCost: number;
    totalOutputCost: number;
    totalCost: number;
    avgTokensPerRequest: number;
    avgCostPerRequest: number;
    modelStats: Record<string, {
      count: number;
      totalTokens: number;
      totalInputTokens: number;
      totalOutputTokens: number;
      totalInputCost: number;
      totalOutputCost: number;
      totalCost: number;
    }>;
    operationStats: Record<string, {
      count: number;
      totalTokens: number;
      totalInputTokens: number;
      totalOutputTokens: number;
      totalInputCost: number;
      totalOutputCost: number;
      totalCost: number;
    }>;
    taskStats: LLMStats[];
  }> {
    if (taskIds.length === 0) {
      return {
        totalTaskIds: 0,
        totalRequests: 0,
        totalTokens: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalInputCost: 0,
        totalOutputCost: 0,
        totalCost: 0,
        avgTokensPerRequest: 0,
        avgCostPerRequest: 0,
        modelStats: {},
        operationStats: {},
        taskStats: []
      };
    }

    try {
      // Получаем статистику для всех taskId параллельно
      const statsPromises = taskIds.map(taskId => this.getStatsForTaskId(taskId));
      const taskStats = await Promise.all(statsPromises);

      // Агрегируем статистику
      const aggregated = {
        totalTaskIds: taskStats.length,
        totalRequests: 0,
        totalTokens: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalInputCost: 0,
        totalOutputCost: 0,
        totalCost: 0,
        avgTokensPerRequest: 0,
        avgCostPerRequest: 0,
        modelStats: {} as Record<string, { count: number; totalTokens: number; totalInputTokens: number; totalOutputTokens: number; totalInputCost: number; totalOutputCost: number; totalCost: number; }>,
        operationStats: {} as Record<string, { count: number; totalTokens: number; totalInputTokens: number; totalOutputTokens: number; totalInputCost: number; totalOutputCost: number; totalCost: number; }>,
        taskStats
      };

      // Суммируем общие показатели
      taskStats.forEach(task => {
        aggregated.totalRequests += task.totalRequests;
        aggregated.totalTokens += task.totalTokens;
        aggregated.totalCost += task.totalCost;

        // Суммируем входные и выходные токены и их стоимость из всех запросов
        task.requests.forEach(request => {
          aggregated.totalInputTokens += request.costData.promptTokens;
          aggregated.totalOutputTokens += request.costData.completionTokens;
          aggregated.totalInputCost += request.costData.estimatedCost.inputCost;
          aggregated.totalOutputCost += request.costData.estimatedCost.outputCost;
        });

        // Агрегируем статистику по моделям
        Object.entries(task.modelStats).forEach(([model, stats]) => {
          if (!aggregated.modelStats[model]) {
            aggregated.modelStats[model] = { count: 0, totalTokens: 0, totalInputTokens: 0, totalOutputTokens: 0, totalInputCost: 0, totalOutputCost: 0, totalCost: 0 };
          }
          aggregated.modelStats[model].count += stats.count;
          aggregated.modelStats[model].totalTokens += stats.totalTokens;
          aggregated.modelStats[model].totalCost += stats.totalCost;

          // Суммируем входные и выходные токены и их стоимость для этой модели
          task.requests
            .filter(request => request.costData.model === model)
            .forEach(request => {
              aggregated.modelStats[model].totalInputTokens += request.costData.promptTokens;
              aggregated.modelStats[model].totalOutputTokens += request.costData.completionTokens;
              aggregated.modelStats[model].totalInputCost += request.costData.estimatedCost.inputCost;
              aggregated.modelStats[model].totalOutputCost += request.costData.estimatedCost.outputCost;
            });
        });

        // Агрегируем статистику по операциям
        task.requests.forEach(request => {
          const operation = request.operation;
          if (!aggregated.operationStats[operation]) {
            aggregated.operationStats[operation] = { count: 0, totalTokens: 0, totalInputTokens: 0, totalOutputTokens: 0, totalInputCost: 0, totalOutputCost: 0, totalCost: 0 };
          }
          aggregated.operationStats[operation].count += 1;
          aggregated.operationStats[operation].totalTokens += request.costData.totalTokens;
          aggregated.operationStats[operation].totalInputTokens += request.costData.promptTokens;
          aggregated.operationStats[operation].totalOutputTokens += request.costData.completionTokens;
          aggregated.operationStats[operation].totalInputCost += request.costData.estimatedCost.inputCost;
          aggregated.operationStats[operation].totalOutputCost += request.costData.estimatedCost.outputCost;
          aggregated.operationStats[operation].totalCost += request.costData.estimatedCost.totalCost;
        });
      });

      // Вычисляем средние значения
      if (aggregated.totalRequests > 0) {
        aggregated.avgTokensPerRequest = aggregated.totalTokens / aggregated.totalRequests;
        aggregated.avgCostPerRequest = aggregated.totalCost / aggregated.totalRequests;
      }

      return aggregated;
    } catch (error) {
      console.error('Ошибка при агрегации статистики:', error);
      throw error;
    }
  }

  /**
   * Форматировать стоимость для отображения
   */
  formatCost(cost: number): string {
    // Всегда отображаем в долларах с 4 знаками после запятой для единообразия
    return `$${cost.toFixed(4)}`;
  }

  /**
   * Форматировать количество токенов
   */
  formatTokens(tokens: number): string {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  }
}

export const llmStatsService = new LLMStatsService();

